import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Send, Building2, MessageCircle, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Contact Sport Fitt Pro | Customer Support';
  }, []);

  const addressLine2 = [CONTACT_INFO.address.city, CONTACT_INFO.address.state, CONTACT_INFO.address.zip]
    .filter(Boolean).join(' ').trim();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 1000));
    setIsSubmitting(false);
    setSubmitted(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* Banner */}
        <section className="relative h-80 overflow-hidden bg-ink-950">
          <img src={siteImages.contactBanner} alt="Contact" className="absolute inset-0 w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/80 to-transparent" />
          <div className="absolute bottom-0 right-0 w-2/5 h-2 bg-athletic-500" />
          <div className="relative h-full max-w-7xl mx-auto px-4 lg:px-8 flex items-end pb-12">
            <div>
              <nav className="flex items-center gap-2 text-white/60 text-xs font-bold uppercase tracking-wider mb-4">
                <Link to="/" className="hover:text-athletic-400 transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-white">Contact</span>
              </nav>
              <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Get In Touch</p>
              <h1 className="font-display text-5xl md:text-7xl text-white leading-none tracking-wider">
                TALK TO <span className="text-athletic-500">OUR TEAM</span>
              </h1>
              <p className="text-white/70 mt-4 max-w-lg text-sm">
                Questions about products, orders or returns? We're athletes helping athletes — real humans, fast replies.
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
          <div className="grid lg:grid-cols-3 gap-8">

            {/* Contact Info */}
            <div className="space-y-5">
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="block bg-white border-2 border-ink-100 hover:border-athletic-500 p-6 transition-all group">
                <div className="w-12 h-12 bg-athletic-500 flex items-center justify-center mb-4">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <p className="text-[10px] text-ink-400 font-bold uppercase tracking-[0.3em] mb-1">Phone</p>
                <p className="font-display text-2xl text-ink-900 tracking-wider group-hover:text-athletic-500 transition-colors">{CONTACT_INFO.phone}</p>
                <p className="text-ink-500 text-xs mt-2">Mon-Fri 9AM-5PM EST</p>
              </a>

              <a href={`mailto:${CONTACT_INFO.email}`} className="block bg-white border-2 border-ink-100 hover:border-athletic-500 p-6 transition-all group">
                <div className="w-12 h-12 bg-athletic-500 flex items-center justify-center mb-4">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <p className="text-[10px] text-ink-400 font-bold uppercase tracking-[0.3em] mb-1">Email</p>
                <p className="font-bold text-lg text-ink-900 break-all group-hover:text-athletic-500 transition-colors">{CONTACT_INFO.email}</p>
                <p className="text-ink-500 text-xs mt-2">Reply within 24 hours</p>
              </a>

              <div className="bg-white border-2 border-ink-100 p-6">
                <div className="w-12 h-12 bg-athletic-500 flex items-center justify-center mb-4">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <p className="text-[10px] text-ink-400 font-bold uppercase tracking-[0.3em] mb-1">Address</p>
                <p className="text-ink-900 font-bold leading-relaxed">
                  {CONTACT_INFO.address.street}
                  {addressLine2 ? <><br />{addressLine2}</> : null}
                  {CONTACT_INFO.address.country ? <><br />{CONTACT_INFO.address.country}</> : null}
                </p>
              </div>

              {/* Corporate Name */}
              <div className="bg-ink-950 text-white p-6 border-l-4 border-athletic-500">
                <div className="flex items-start gap-3">
                  <Building2 className="w-6 h-6 text-athletic-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] text-white/50 font-bold uppercase tracking-[0.3em] mb-1">Corporate</p>
                    <p className="font-display text-2xl tracking-wider">{CONTACT_INFO.corpName}</p>
                    <p className="text-white/60 text-xs mt-2">
                      Address: {CONTACT_INFO.address.street}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-ink-50 border-2 border-ink-100 p-6">
                <div className="w-12 h-12 bg-volt-400 flex items-center justify-center mb-4">
                  <Clock className="w-5 h-5 text-ink-900" />
                </div>
                <p className="text-[10px] text-ink-400 font-bold uppercase tracking-[0.3em] mb-3">Hours</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-ink-500">Mon – Fri</span><span className="text-ink-900 font-bold">9 AM – 5 PM</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">Saturday</span><span className="text-ink-900 font-bold">10 AM – 2 PM</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">Sunday</span><span className="text-ink-400">Closed</span></div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white border-2 border-ink-100 p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-2">
                  <MessageCircle className="w-6 h-6 text-athletic-500" />
                  <p className="text-athletic-500 text-[11px] font-black tracking-[0.4em] uppercase">Send a Message</p>
                </div>
                <h2 className="font-display text-4xl text-ink-900 tracking-wider mb-2">WE'RE LISTENING</h2>
                <p className="text-ink-500 mb-8">Fill out the form below. Real humans respond within 24 hours.</p>

                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-athletic-500 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Send className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-display text-3xl text-ink-900 tracking-wider mb-2">MESSAGE SENT!</h3>
                    <p className="text-ink-500 mb-6">We'll get back to you within 24 hours.</p>
                    <button onClick={() => setSubmitted(false)} className="px-6 py-3 bg-athletic-500 text-white font-black uppercase tracking-wider text-sm hover:bg-athletic-600 transition-colors">
                      Send Another
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[11px] font-black uppercase tracking-wider text-ink-900 mb-2">Your Name</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="John Doe" className="w-full px-4 py-3 bg-ink-50 border-2 border-ink-100 focus:outline-none focus:border-athletic-500 text-ink-900 transition-colors" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-black uppercase tracking-wider text-ink-900 mb-2">Email</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@email.com" className="w-full px-4 py-3 bg-ink-50 border-2 border-ink-100 focus:outline-none focus:border-athletic-500 text-ink-900 transition-colors" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[11px] font-black uppercase tracking-wider text-ink-900 mb-2">Subject</label>
                      <select name="subject" value={formData.subject} onChange={handleChange} required className="w-full px-4 py-3 bg-ink-50 border-2 border-ink-100 focus:outline-none focus:border-athletic-500 text-ink-900 cursor-pointer">
                        <option value="">Select a subject</option>
                        <option value="order">Order Inquiry</option>
                        <option value="product">Product Question</option>
                        <option value="shipping">Shipping</option>
                        <option value="returns">Returns & Refunds</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-black uppercase tracking-wider text-ink-900 mb-2">Message</label>
                      <textarea name="message" value={formData.message} onChange={handleChange} required rows={6} placeholder="How can we help you?" className="w-full px-4 py-3 bg-ink-50 border-2 border-ink-100 focus:outline-none focus:border-athletic-500 text-ink-900 resize-none transition-colors" />
                    </div>
                    <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-black uppercase tracking-wider disabled:opacity-50 transition-all">
                      {isSubmitting ? (
                        <><span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Sending...</>
                      ) : (
                        <><Send className="w-5 h-5" /> Send Message</>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
